import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { apps } from '../data/mock';
import { ExternalLink, Lock } from 'lucide-react';

const AppsSection = () => {
  const { language, t, isRTL } = useLanguage();

  const comingSoonText = {
    ar: 'قريباً',
    en: 'Coming Soon'
  };

  return (
    <section 
      id="apps" 
      className="relative py-32 bg-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-5">
        <div className="w-full h-full" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
          {t.apps.title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          {/* Coming Soon App - Left */}
          <div className="relative block">
            <div className="relative bg-white/5 border border-white/10 p-8 rounded-none">
              {/* Blurred App Icon */}
              <div className="w-40 h-40 md:w-44 md:h-44 mx-auto mb-6 overflow-hidden rounded-2xl relative">
                <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-800 blur-md"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock size={48} className="text-white/50" />
                </div>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-white/50 text-center mb-3">
                ???
              </h3>

              <p className="text-white/40 text-center mb-6">
                {comingSoonText[language]}
              </p>

              <div className="flex items-center justify-center gap-2 text-white/30">
                <span>{comingSoonText[language]}</span>
              </div>

              {/* Blur overlay */}
              <div className="absolute inset-0 backdrop-blur-[2px] bg-black/20 rounded-none"></div>
            </div>
          </div>

          {/* ShopVerse - Center (Main) */}
          {apps.map((app) => (
            <a
              key={app.id}
              href={app.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block transform scale-105"
            >
              <div className="relative bg-white/5 border border-white/20 p-8 rounded-none hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:border-white/30">
                {/* App Icon - keeping natural colors */}
                <div className="w-44 h-44 md:w-52 md:h-52 mx-auto mb-6 overflow-hidden !rounded-[50px]">
                  <img 
                    src={app.icon} 
                    alt={app.name}
                    className="w-full h-full object-cover !rounded-[50px]"
                  />
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-3">
                  {app.name}
                </h3>

                <p className="text-white/60 text-center mb-6">
                  {app.description[language]}
                </p>

                <div className="flex items-center justify-center gap-2 text-white/80 group-hover:text-white transition-colors">
                  <span>{t.apps.viewApp}</span>
                  <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform" />
                </div>

                {/* Hover glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent"></div>
                </div>
              </div>
            </a>
          ))}

          {/* Coming Soon App - Right */}
          <div className="relative block">
            <div className="relative bg-white/5 border border-white/10 p-8 rounded-none">
              {/* Blurred App Icon */}
              <div className="w-40 h-40 md:w-44 md:h-44 mx-auto mb-6 overflow-hidden rounded-2xl relative">
                <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 blur-md"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock size={48} className="text-white/50" />
                </div>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-white/50 text-center mb-3">
                ???
              </h3>

              <p className="text-white/40 text-center mb-6">
                {comingSoonText[language]}
              </p>

              <div className="flex items-center justify-center gap-2 text-white/30">
                <span>{comingSoonText[language]}</span>
              </div>

              {/* Blur overlay */}
              <div className="absolute inset-0 backdrop-blur-[2px] bg-black/20 rounded-none"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppsSection;
